/**
 * Platform layout data — level platform geometry as plain data, so a level's shape
 * can be linted (tests/unit-vitest/layout-reachability.spec.ts) and tuned
 * without reading scene-construction code.
 *
 * Coordinates are the collider box centre, in world units, before the view
 * mirror (VIEW.MIRROR_X) — +x is "forward" through the level.
 */
export type PlatformRoute =
  /** Both cats must be able to get onto this with an ordinary jump. */
  | "jump"
  /** Only Minna needs to reach it with an ordinary jump (Basil stays below). */
  | "minna"
  /**
   * Needs more than an ordinary jump from the ground/platform chain:
   * Overclocked Leap, a puzzle lift or prop, or a drop from above.
   */
  | "assisted";

export interface PlatformDef {
  name: string;
  /** Width along x. */
  w: number;
  /** Height (thickness), default 0.4. */
  h?: number;
  /** Depth along z, default 2. */
  d?: number;
  /** Box centre. */
  x: number;
  y: number;
  /** How players are expected to get on top. Default "jump". */
  route?: PlatformRoute;
  /** Why the route is what it is — shown in lint failures. */
  note?: string;
}

export interface PlatformLayout {
  level: string;
  /** Top of the ground slab (PhysicsSystem.createGround puts it at y=0). */
  groundTopY: number;
  platforms: readonly PlatformDef[];
  /**
   * Static colliders a scene builds on its own (a dais, a set piece) that
   * players can still stand on. Counted by the reachability lint only.
   */
  extraColliders?: readonly PlatformDef[];
}

export const DEFAULT_PLATFORM_HEIGHT = 0.4;
export const DEFAULT_PLATFORM_DEPTH = 2;

export function platformHeight(def: PlatformDef): number {
  return def.h ?? DEFAULT_PLATFORM_HEIGHT;
}

export function platformDepth(def: PlatformDef): number {
  return def.d ?? DEFAULT_PLATFORM_DEPTH;
}

export function platformTop(def: PlatformDef): number {
  return def.y + platformHeight(def) / 2;
}

export function platformBottom(def: PlatformDef): number {
  return def.y - platformHeight(def) / 2;
}

export function platformLeft(def: PlatformDef): number {
  return def.x - def.w / 2;
}

export function platformRight(def: PlatformDef): number {
  return def.x + def.w / 2;
}
